import React from 'react';
import { ZoomLevel, TripIntent, City, VehicleType } from './types';
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  FormControl,
} from "@mui/material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ClearIcon from '@mui/icons-material/Clear';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Calendar, ChevronLeft, ChevronRight, Search, ZoomIn, ZoomOut, MapPin, Clock, Car, X, CheckCircle, ArrowLeftRight } from 'lucide-react';

interface SchedulerHeaderProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  zoomLevel: ZoomLevel;
  onZoomLevelChange: (level: ZoomLevel) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onTripIntentChange: (tripIntent: TripIntent) => void;
  onShowAvailability: (tripIntent: TripIntent) => void;
  onClearAvailability: () => void;
  hasActiveWindow: boolean;
}
const inputStyles = {
  bgcolor: 'white',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ddd' },
    '&:hover fieldset': { borderColor: '#999' },
    '&.Mui-focused fieldset': { borderColor: '#1976d2' },
    '& input': { color: '#333' }
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': { color: '#1976d2' }
  }
};

const selectStyles = {
  bgcolor: 'white',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ddd' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
  '& .MuiSelect-select': { color: '#333' }
};
type TripType = 'one-way' | 'local-6h' | 'local-9h' | 'local-12h' | 'outstation-round';

export function SchedulerHeader({
  selectedDate,
  onDateChange,
  zoomLevel,
  onZoomLevelChange,
  searchQuery,
  onSearchChange,
  onTripIntentChange,
  onShowAvailability,
  onClearAvailability,
  hasActiveWindow
}: SchedulerHeaderProps) {
  const [tripType, setTripType] = React.useState<TripType>('one-way');
  const [pickupCity, setPickupCity] = React.useState<City>('Pune');
  const [dropCity, setDropCity] = React.useState<City>('Mumbai');
  const [pickupDate, setPickupDate] = React.useState(new Date().toISOString().slice(0, 16));
  const [pickupDateOnly, setPickupDateOnly] = React.useState(new Date().toISOString().slice(0, 10));
  const [pickupTimeHours, setPickupTimeHours] = React.useState('10');
  const [pickupTimeMinutes, setPickupTimeMinutes] = React.useState('0');
  const [returnDate, setReturnDate] = React.useState(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16));
  const [returnDateOnly, setReturnDateOnly] = React.useState(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [returnTimeHours, setReturnTimeHours] = React.useState('10');
  const [returnTimeMinutes, setReturnTimeMinutes] = React.useState('0');
  const [vehicleType, setVehicleType] = React.useState<VehicleType | ''>('');
  const [estimatedDuration, setEstimatedDuration] = React.useState(180);

  // Sync datetime when date or time changes
  React.useEffect(() => {
    const pickupTimeOnly = `${pickupTimeHours.padStart(2, '0')}:${pickupTimeMinutes.padStart(2, '0')}`;
    setPickupDate(`${pickupDateOnly}T${pickupTimeOnly}`);
  }, [pickupDateOnly, pickupTimeHours, pickupTimeMinutes]);

  React.useEffect(() => {
    const returnTimeOnly = `${returnTimeHours.padStart(2, '0')}:${returnTimeMinutes.padStart(2, '0')}`;
    setReturnDate(`${returnDateOnly}T${returnTimeOnly}`);
  }, [returnDateOnly, returnTimeHours, returnTimeMinutes]);

  // Track previous values to detect changes
  const prevValuesRef = React.useRef({
    tripType,
    pickupCity,
    pickupDate,
    dropCity,
    estimatedDuration,
    vehicleType
  });

  const isLocalTrip = tripType.startsWith('local-') || tripType === 'outstation-round';
  const isOneWay = tripType === 'one-way';

  const handleSwapCities = () => {
    const temp = pickupCity;
    setPickupCity(dropCity);
    setDropCity(temp);
  };

  React.useEffect(() => {
    switch (tripType) {
      case 'one-way':
        setEstimatedDuration(300); // 5 hours (conservative estimate for Pune ⇄ Mumbai)
        if (pickupCity === dropCity) {
          setDropCity(pickupCity === 'Pune' ? 'Mumbai' : 'Pune');
        }
        break;
      case 'local-6h':
        setEstimatedDuration(360);
        setDropCity(pickupCity);
        break;
      case 'local-9h':
        setEstimatedDuration(540);
        setDropCity(pickupCity);
        break;
      case 'local-12h':
        setEstimatedDuration(720);
        setDropCity(pickupCity);
        break;
      case 'outstation-round':
        // Calculate duration based on return time
        const pickup = new Date(pickupDate);
        const returnTime = new Date(returnDate);
        const durationMinutes = Math.max(0, (returnTime.getTime() - pickup.getTime()) / 60000);
        setEstimatedDuration(durationMinutes || 1440); // Default to 24 hours if invalid
        setDropCity(pickupCity);
        break;
    }
  }, [tripType, pickupCity, pickupDate, returnDate]);

  React.useEffect(() => {
    const tripIntent: TripIntent = {
      tripType,
      pickupCity,
      pickupLocation: '',
      pickupDateTime: pickupDate,
      dropCity: isLocalTrip ? pickupCity : dropCity,
      dropLocation: '',
      estimatedDuration,
      ...(vehicleType && { vehicleTypeRequired: vehicleType })
    };

    // Always update trip intent
    onTripIntentChange(tripIntent);

    // Check if values actually changed
    const prevValues = prevValuesRef.current;
    const hasChanges =
      prevValues.tripType !== tripType ||
      prevValues.pickupCity !== pickupCity ||
      prevValues.pickupDate !== pickupDate ||
      prevValues.dropCity !== dropCity ||
      prevValues.estimatedDuration !== estimatedDuration ||
      prevValues.vehicleType !== vehicleType;

    // Auto-update availability window if it's already active AND values changed
    if (hasActiveWindow && hasChanges) {
      onShowAvailability(tripIntent);
    }

    // Update ref for next comparison
    prevValuesRef.current = {
      tripType,
      pickupCity,
      pickupDate,
      dropCity,
      estimatedDuration,
      vehicleType
    };
  }, [tripType, pickupCity, pickupDate, dropCity, estimatedDuration, vehicleType, isLocalTrip, hasActiveWindow]);

  const handleShowAvailability = () => {
    const tripIntent: TripIntent = {
      tripType,
      pickupCity,
      pickupLocation: '',
      pickupDateTime: pickupDate,
      dropCity: isLocalTrip ? pickupCity : dropCity,
      dropLocation: '',
      estimatedDuration,
      ...(vehicleType && { vehicleTypeRequired: vehicleType })
    };
    onShowAvailability(tripIntent);
  };

  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    onDateChange(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    onDateChange(newDate);
  };

  const handleToday = () => {
    onDateChange(new Date());
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleZoomOut = () => {
    const levels: ZoomLevel[] = [ '2h', '4h', '8h', '1d'];
    const currentIndex = levels.indexOf(zoomLevel);
    if (currentIndex < levels.length - 1) {
      onZoomLevelChange(levels[currentIndex + 1]);
    }
  };

  const handleZoomIn = () => {
    const levels: ZoomLevel[] = [ '2h', '4h', '8h', '1d'];
    const currentIndex = levels.indexOf(zoomLevel);
    if (currentIndex > 0) {
      onZoomLevelChange(levels[currentIndex - 1]);
    }
  };

  return (
    <Box sx={{ borderBottom: "1px solid #e0e0e0", bgcolor: "#f5f5f5", px: 1.5, py: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography fontWeight={600} fontSize="15px" sx={{ color: '#333', mr: 0 }}>
          Vehicle Scheduler
        </Typography>


        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={tripType}
            onChange={(e) => setTripType(e.target.value as TripType)}
            sx={{
              ...selectStyles,
              '& .MuiSelect-select': {
                color: '#333',
                py: 0.75
              }
            }}
          >
            <MenuItem value="one-way">One-Way (Pune ⇄ Mumbai)</MenuItem>
            <MenuItem value="local-6h">Local 6h Package</MenuItem>
            <MenuItem value="local-9h">Local 9h Package</MenuItem>
            <MenuItem value="local-12h">Local 12h Package</MenuItem>
            <MenuItem value="outstation-round">Outstation Round Trip</MenuItem>
          </Select>
        </FormControl>

        {/* Pickup City & Location or Route Toggle */}
        {isOneWay ? (
          <Button
            variant="outlined"
            onClick={handleSwapCities}
            startIcon={<SwapHorizIcon />}
            sx={{
              color: '#1976d2',
              textTransform: 'none',
              px: 2,
              py: 0.75,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              '&:hover': {
                color: '#1565c0',
                borderColor: '#1976d2'
              }
            }}
          >
            {pickupCity} ⇄ {dropCity}
          </Button>
        ) : (
          <FormControl size="small">
            <Select
              value={pickupCity}
              onChange={(e) => setPickupCity(e.target.value as City)}
                sx={{
              ...selectStyles,
              '& .MuiSelect-select': { 
                color: '#333',
                py: 0.75
              }
            }}             >
              <MenuItem value="Pune">Pune</MenuItem>
              <MenuItem value="Mumbai">Mumbai</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Arrow and Drop City for non-local, non-one-way trips */}
        {!isLocalTrip && !isOneWay && (
          <>
            <Typography sx={{ color: '#999', px: 0.5 }}>→</Typography>
            <FormControl size="small">
              <Select
                value={dropCity}
                onChange={(e) => setDropCity(e.target.value as City)}
                sx={{
                  ...selectStyles,
                  '& .MuiSelect-select': { 
                    color: '#333',
                    py: 0.75
                  }
                }}
              >
                <MenuItem value="Pune">Pune</MenuItem>
                <MenuItem value="Mumbai">Mumbai</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        {/* Pickup Date Time */}
        <IconButton size="small" sx={{ color: '#666', p: 0.5 }}>
          <AccessTimeIcon fontSize="small" />
        </IconButton>

        <TextField
          size="small"
          type="date"
          value={pickupDateOnly}
          onChange={(e) => setPickupDateOnly(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            ...inputStyles,
            width: 150,
            '& .MuiInputBase-input': { py: 0.75 }
          }}
        />

        <TextField
          size="small"
          type="number"
          value={pickupTimeHours}
          onChange={(e) => {
            const val = Math.max(0, Math.min(23, Number(e.target.value)));
            setPickupTimeHours(val.toString());
          }}
          inputProps={{ min: 0, max: 23 }}
          sx={{
            ...inputStyles,
            width: 60,
            '& .MuiInputBase-input': { py: 0.75, textAlign: 'center' }
          }}
        />

        <Typography sx={{ color: '#666', px: 0.5 }}>:</Typography>

        <TextField
          size="small"
          type="number"
          value={pickupTimeMinutes}
          onChange={(e) => {
            const val = Math.max(0, Math.min(59, Number(e.target.value)));
            setPickupTimeMinutes(val.toString());
          }}
          inputProps={{ min: 0, max: 59 }}
          sx={{
            ...inputStyles,
            width: 60,
            '& .MuiInputBase-input': { py: 0.75, textAlign: 'center' }
          }}
        />

        {/* Return Date Time for Outstation Round Trip */}
        {tripType === 'outstation-round' && (
          <>
            <Typography sx={{ color: '#666', px: 0.5, fontSize: '13px' }}>to</Typography>
            <TextField
              size="small"
              type="date"
              value={returnDateOnly}
              onChange={(e) => setReturnDateOnly(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...inputStyles,
                width: 150,
                '& .MuiInputBase-input': { py: 0.75 }
              }}
            />
            <TextField
              size="small"
              type="number"
              value={returnTimeHours}
              onChange={(e) => {
                const val = Math.max(0, Math.min(23, Number(e.target.value)));
                setReturnTimeHours(val.toString());
              }}
              inputProps={{ min: 0, max: 23 }}
              sx={{
                ...inputStyles,
                width: 60,
                '& .MuiInputBase-input': { py: 0.75, textAlign: 'center' }
              }}
            />
            <Typography sx={{ color: '#666', px: 0.5 }}>:</Typography>
            <TextField
              size="small"
              type="number"
              value={returnTimeMinutes}
              onChange={(e) => {
                const val = Math.max(0, Math.min(59, Number(e.target.value)));
                setReturnTimeMinutes(val.toString());
              }}
              inputProps={{ min: 0, max: 59 }}
              sx={{
                ...inputStyles,
                width: 60,
                '& .MuiInputBase-input': { py: 0.75, textAlign: 'center' }
              }}
            />
          </>
        )}

        {/* Vehicle Type */}
        <IconButton size="small" sx={{ color: '#666', p: 0.5 }}>
          <Car fontSize="small" />
        </IconButton>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value as VehicleType | '')}
            displayEmpty
            sx={{
              ...selectStyles,
              '& .MuiSelect-select': { 
                color: '#333',
                py: 0.75
              }
            }}
          >
            <MenuItem value="">Any Vehicle</MenuItem>
            <MenuItem value="Maruti Suzuki Dzire">Maruti Suzuki Dzire</MenuItem>
            <MenuItem value="Toyota Rumion S">Toyota Rumion S</MenuItem>
            <MenuItem value="Kia Carens Premium">Kia Carens Premium</MenuItem>
            <MenuItem value="Toyota Crysta GX 7 STR">Toyota Crysta GX 7 STR</MenuItem>
          </Select>
        </FormControl>

        {/* Clear Button */}
        {hasActiveWindow && (
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={onClearAvailability}
            sx={{
              color: '#666',
              borderColor: '#ddd',
              textTransform: 'none',
              px: 2,
              py: 0.6,
              '&:hover': {
                borderColor: '#999',
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Clear
          </Button>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Zoom Controls */}
        <IconButton 
          size="small" 
          onClick={handleZoomOut} 
          disabled={zoomLevel === '1d'} 
          sx={{ color: '#666' }}
        >
          <ZoomOutIcon />
        </IconButton>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={zoomLevel}
            onChange={(e) => onZoomLevelChange(e.target.value as ZoomLevel)}
            sx={{
              ...selectStyles,
              '& .MuiSelect-select': { 
                color: '#333',
                py: 0.75
              }
            }}
          >
            {/* <MenuItem value="1h">1 Hour</MenuItem> */}
            <MenuItem value="2h">2 Hours</MenuItem>
            <MenuItem value="4h">4 Hours</MenuItem>
            <MenuItem value="8h">8 Hours</MenuItem>
            <MenuItem value="1d">1 Day</MenuItem>
          </Select>
        </FormControl>

        <IconButton 
          size="small" 
          onClick={handleZoomIn} 
          // disabled={zoomLevel === '1h'} 
          sx={{ color: '#666' }}
        >
          <ZoomInIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}